/**
 * Column statistics: full profiles for the side panel and compact header summaries.
 *
 * Performance target: summarizeAll over 200 rows × 200 columns < 20 ms.
 * Histogram algorithm: Freedman–Diaconis bin width 2·IQR·n^(-1/3), falling back to
 * Sturges (ceil(log2 n)+1) when IQR is 0 or n<4. Clamp to 1–20 bins.
 */

import { toEpochMs } from '../format';
import { getElementValues, getValue, isMissing } from './flatten';
import type {
  Bin,
  ColumnDef,
  ColumnProfile,
  HeaderSummary,
  ProfileColumn,
  Row,
  SummarizeColumn,
  ValueCount,
  ValueKind,
} from './types';

/** Key for deduplication: objects/arrays use JSON, primitives use type-prefixed string. */
function distinctKey(v: unknown): string {
  if (v === null) return 'null';
  if (typeof v === 'object') return JSON.stringify(v);
  return `${typeof v}:${String(v)}`;
}

/** Population standard deviation (divide by n, not n-1). */
function populationStd(values: number[], mean: number): number {
  if (values.length === 0) return 0;
  const variance = values.reduce((sum, x) => sum + (x - mean) ** 2, 0) / values.length;
  return Math.sqrt(variance);
}

/** Median of sorted numbers (average of middle two for even n). */
function median(sorted: number[]): number {
  const n = sorted.length;
  if (n === 0) return 0;
  if (n % 2 === 1) return sorted[Math.floor(n / 2)];
  return (sorted[n / 2 - 1] + sorted[n / 2]) / 2;
}

/** Numeric histogram with Freedman–Diaconis / Sturges. Bins [x0, x1) except last is inclusive. */
function histogram(values: number[]): Bin[] {
  const n = values.length;
  if (n === 0) return [];

  const sorted = [...values].sort((a, b) => a - b);
  const min = sorted[0];
  const max = sorted[n - 1];

  if (min === max) return [{ x0: min, x1: max, count: n }];

  let binCount = 1;
  if (n >= 4) {
    const q1 = sorted[Math.floor(n * 0.25)];
    const q3 = sorted[Math.floor(n * 0.75)];
    const iqr = q3 - q1;

    if (iqr > 0) {
      const binWidth = 2 * iqr * Math.pow(n, -1 / 3);
      binCount = Math.ceil((max - min) / binWidth);
    } else {
      binCount = Math.ceil(Math.log2(n)) + 1;
    }
  } else {
    binCount = Math.ceil(Math.log2(n)) + 1;
  }

  binCount = Math.max(1, Math.min(20, binCount));

  const binWidth = (max - min) / binCount;
  const bins: Bin[] = [];

  for (let i = 0; i < binCount; i++) {
    bins.push({
      x0: min + i * binWidth,
      x1: min + (i + 1) * binWidth,
      count: 0,
    });
  }

  for (const v of sorted) {
    let binIdx = Math.floor((v - min) / binWidth);
    if (binIdx >= binCount) binIdx = binCount - 1;
    bins[binIdx].count++;
  }

  return bins;
}

/** Date histogram with snapped bin edges (hour/day/week boundaries in UTC). Cap at 30 bins. */
function dateHistogram(epochValues: number[]): Bin[] {
  const n = epochValues.length;
  if (n === 0) return [];

  const sorted = [...epochValues].sort((a, b) => a - b);
  const min = sorted[0];
  const max = sorted[n - 1];
  const spanMs = max - min;

  if (min === max) return [{ x0: min, x1: max, count: n }];

  const HOUR = 3600000;
  const DAY = 86400000;
  const WEEK = 7 * DAY;

  let binSize: number;
  let snapFn: (ms: number) => number;

  if (spanMs <= 2 * DAY) {
    // Hour boundaries
    binSize = HOUR;
    snapFn = (ms) => Math.floor(ms / HOUR) * HOUR;
  } else if (spanMs <= 120 * DAY) {
    // Day boundaries
    binSize = DAY;
    snapFn = (ms) => {
      const d = new Date(ms);
      return Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
    };
  } else {
    // Week boundaries (Monday)
    binSize = WEEK;
    snapFn = (ms) => {
      const d = new Date(ms);
      const day = d.getUTCDay();
      const diff = (day + 6) % 7; // Days since Monday
      return Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate() - diff);
    };
  }

  const minSnap = snapFn(min);
  const maxSnap = snapFn(max);
  let binCount = Math.floor((maxSnap - minSnap) / binSize) + 1;

  // If we'd have too many bins, merge into wider bins
  if (binCount > 30) {
    const targetBinCount = 30;
    const mergeFactor = Math.ceil(binCount / targetBinCount);
    binSize *= mergeFactor;
    binCount = Math.ceil(binCount / mergeFactor);
  }

  const bins: Bin[] = [];
  for (let i = 0; i < binCount; i++) {
    bins.push({
      x0: minSnap + i * binSize,
      x1: minSnap + (i + 1) * binSize,
      count: 0,
    });
  }

  for (const v of sorted) {
    let binIdx = Math.floor((snapFn(v) - minSnap) / binSize);
    if (binIdx < 0) binIdx = 0;
    if (binIdx >= binCount) binIdx = binCount - 1;
    bins[binIdx].count++;
  }

  return bins;
}

/** Top N values by count, ties broken by value ascending. */
function topValues(values: unknown[], limit: number): ValueCount[] {
  const counts = new Map<string, { value: string; count: number }>();

  for (const v of values) {
    const key = distinctKey(v);
    const existing = counts.get(key);
    if (existing) {
      existing.count++;
    } else {
      counts.set(key, { value: String(v), count: 1 });
    }
  }

  return [...counts.values()]
    .sort((a, b) => {
      if (b.count !== a.count) return b.count - a.count;
      return a.value.localeCompare(b.value);
    })
    .slice(0, limit);
}

export const profileColumn: ProfileColumn = (rows, indices, column) => {
  const base = indices.length;
  const values: unknown[] = [];

  for (const idx of indices) {
    const v = getValue(rows[idx], column);
    if (!isMissing(v)) values.push(v);
  }

  const count = values.length;
  const missing = base - count;

  // Distinct count
  const distinctKeys = new Set<string>();
  for (const v of values) {
    distinctKeys.add(distinctKey(v));
  }
  const distinct = distinctKeys.size;

  const profile: ColumnProfile = {
    column: column.id,
    base,
    count,
    missing,
    distinct,
  };

  // Early exit if no non-missing values
  if (count === 0) return profile;

  // Type-specific statistics
  if (column.type === 'number') {
    const nums = values.filter((v): v is number => typeof v === 'number' && Number.isFinite(v));
    if (nums.length > 0) {
      const sorted = [...nums].sort((a, b) => a - b);
      const min = sorted[0];
      const max = sorted[sorted.length - 1];
      const mean = nums.reduce((sum, x) => sum + x, 0) / nums.length;
      const med = median(sorted);
      const std = populationStd(nums, mean);

      profile.number = {
        min,
        max,
        mean,
        median: med,
        std,
        histogram: histogram(nums),
      };
    }
  } else if (column.type === 'date') {
    const dates: number[] = [];
    let invalid = 0;

    for (const v of values) {
      const ms = toEpochMs(v);
      if (ms !== null) {
        dates.push(ms);
      } else {
        invalid++;
      }
    }

    if (dates.length > 0) {
      const sorted = [...dates].sort((a, b) => a - b);
      const min = sorted[0];
      const max = sorted[sorted.length - 1];

      profile.date = {
        min: new Date(min).toISOString(),
        max: new Date(max).toISOString(),
        invalid,
        histogram: dateHistogram(dates),
      };
    } else if (invalid > 0) {
      profile.date = {
        min: '',
        max: '',
        invalid,
        histogram: [],
      };
    }
  } else if (column.type === 'text') {
    const top = topValues(values, 20);

    const strings = values.filter((v): v is string => typeof v === 'string');
    if (strings.length > 0) {
      const lengths = strings.map((s) => s.length);
      const minLen = Math.min(...lengths);
      const maxLen = Math.max(...lengths);
      const meanLen = lengths.reduce((sum, len) => sum + len, 0) / lengths.length;

      profile.text = {
        top,
        length: { min: minLen, mean: meanLen, max: maxLen },
      };
    } else {
      profile.text = { top, length: { min: 0, mean: 0, max: 0 } };
    }
  } else if (column.type === 'boolean') {
    let trueCount = 0;
    let falseCount = 0;

    for (const v of values) {
      if (v === true) trueCount++;
      else if (v === false) falseCount++;
    }

    profile.boolean = { true: trueCount, false: falseCount };
  } else if (column.type === 'array') {
    const arrays = values.filter((v): v is unknown[] => Array.isArray(v));

    if (arrays.length > 0) {
      // Length histogram
      const lengths = arrays.map((arr) => arr.length);
      const maxLen = Math.max(...lengths);

      let lengthBins: Bin[];
      if (maxLen <= 20) {
        // One bin per integer length
        lengthBins = [];
        for (let i = 0; i <= maxLen; i++) {
          lengthBins.push({ x0: i, x1: i, count: 0 });
        }
        for (const len of lengths) {
          lengthBins[len].count++;
        }
      } else {
        // Numeric bins
        lengthBins = histogram(lengths);
      }

      // Top elements per path (first 12 paths, top 10 each)
      const topElements: { path: string; values: ValueCount[] }[] = [];
      const pathsToProcess = column.elementPaths.slice(0, 12);

      for (const path of pathsToProcess) {
        const elementValues: unknown[] = [];
        for (const arr of arrays) {
          const vals = getElementValues(arr, path);
          for (const v of vals) {
            if (!isMissing(v)) {
              elementValues.push(v);
            }
          }
        }

        if (elementValues.length > 0) {
          const top = topValues(elementValues, 10);
          topElements.push({ path, values: top });
        }
      }

      profile.array = { lengthHistogram: lengthBins, topElements };
    }
  } else if (column.type === 'mixed') {
    // Count per kind
    const mixed: Partial<Record<ValueKind, number>> = {};
    for (const v of values) {
      if (v === null) continue;
      const kind = Array.isArray(v) ? 'array' : typeof v === 'object' ? 'object' : (typeof v as ValueKind);
      mixed[kind] = (mixed[kind] ?? 0) + 1;
    }
    profile.mixed = mixed;

    // Also include text.top for the panel to show
    const top = topValues(values, 20);
    const stringValues = values.filter((v): v is string => typeof v === 'string');
    if (stringValues.length > 0) {
      const lengths = stringValues.map((s) => s.length);
      const minLen = Math.min(...lengths);
      const maxLen = Math.max(...lengths);
      const meanLen = lengths.reduce((sum, len) => sum + len, 0) / lengths.length;
      profile.text = { top, length: { min: minLen, mean: meanLen, max: maxLen } };
    } else {
      profile.text = { top, length: { min: 0, mean: 0, max: 0 } };
    }
  }
  // object type: just count/missing/distinct, which we already have

  return profile;
};

export const summarizeColumn: SummarizeColumn = (profile, column) => {
  const missingPct = profile.base > 0 ? (100 * profile.missing) / profile.base : 0;

  let spark: HeaderSummary['spark'] = null;

  if (column.type === 'number' && profile.number) {
    spark = { kind: 'bins', bins: profile.number.histogram };
  } else if (column.type === 'date' && profile.date) {
    spark = { kind: 'bins', bins: profile.date.histogram };
  } else if (column.type === 'array' && profile.array) {
    spark = { kind: 'bins', bins: profile.array.lengthHistogram };
  } else if (column.type === 'boolean' && profile.boolean) {
    spark = {
      kind: 'top',
      top: [
        { value: 'true', count: profile.boolean.true },
        { value: 'false', count: profile.boolean.false },
      ],
      total: profile.count,
    };
  } else if (column.type === 'text' && profile.text) {
    spark = {
      kind: 'top',
      top: profile.text.top.slice(0, 5),
      total: profile.count,
    };
  } else if (column.type === 'mixed' && profile.text) {
    spark = {
      kind: 'top',
      top: profile.text.top.slice(0, 5),
      total: profile.count,
    };
  }

  return {
    type: column.type,
    missingPct,
    distinct: profile.distinct,
    spark,
  };
};

/** Convenience: summarize all columns and return a map keyed by column id. */
export function summarizeAll(
  rows: Row[],
  indices: number[],
  columns: ColumnDef[]
): Map<string, HeaderSummary> {
  const summaries = new Map<string, HeaderSummary>();

  for (const column of columns) {
    const profile = profileColumn(rows, indices, column);
    const summary = summarizeColumn(profile, column);
    summaries.set(column.id, summary);
  }

  return summaries;
}
